//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { IScope } from 'angular';
import React from 'react';
import styled from 'styled-components';

import ActionButton from '@components/atoms/action-button/ActionButton';
import Indicator from '@components/atoms/indicator/Indicator';
import SidebarHeaderItem from '@components/atoms/sidebar-items/SidebarHeaderItem';
import SidebarLoadingPlaceholder from '@components/atoms/sidebar-items/SidebarLoadingPlaceholder';
import SidebarPageItemRouteless from '@components/atoms/sidebar-items/SidebarPageItemRouteless';
import Sidebar from '@components/organisms/sidebar/Sidebar';
import makeAngularReactControllerWithScope, {
  applyScope,
} from '@react-bridge/angular-react-widget-with-scope';
import { IStream } from '@streams-drawer/model/IStream';
import {
  StreamsDrawerStreamType,
  getRecommendedStreamName,
  nameToStreamType,
} from '@streams-drawer/streams-drawer-constants';
import { alphabetizeLocaleCompare } from '@utils/arrayUtils';

interface Props {
  streams: IStream[];
  hasRecommendedStreams: boolean;
  isRefreshingStreams: boolean;
  isRecommendationsClient: boolean;
  showRecommendedSummary: boolean;
  isModerationServices: boolean;
  onSummaryClick: () => void;
  noSelectedStream: boolean;
  recommendationStreams: IStream[];
  onStreamClick: ({ stream }: { stream: IStream }) => void;
  selectedStream: IStream;
  onOpenNewStreamModal: () => void;
  taggedByCuralatePendingCount: number;
  connectPendingCount: number;
  shouldNotAllowUgcStreams: boolean;
}

export const StreamsDrawerSidebarReact = ({
  connectPendingCount,
  hasRecommendedStreams,
  isModerationServices,
  isRecommendationsClient,
  isRefreshingStreams,
  noSelectedStream,
  onOpenNewStreamModal,
  onStreamClick,
  onSummaryClick,
  recommendationStreams,
  selectedStream,
  shouldNotAllowUgcStreams,
  showRecommendedSummary,
  streams,
  taggedByCuralatePendingCount,
}: Props) => {
  const hasAlreadyCreatedStream =
    streams.filter(
      (stream) =>
        nameToStreamType(stream) !== StreamsDrawerStreamType.GALLERY_UPLOADS,
    ).length > 0;
  const isUgcOnlyClientAndStreamAlreadyCreated =
    shouldNotAllowUgcStreams && hasAlreadyCreatedStream;
  const shouldShowCreateStreamButton =
    !isModerationServices && !isUgcOnlyClientAndStreamAlreadyCreated;

  const pendingItemsCount = (stream: IStream) => {
    const isTaggedByCuralateStream =
      nameToStreamType(stream) === StreamsDrawerStreamType.TAGGED_BY_CURALTE;
    const isConnectStream =
      nameToStreamType(stream) === StreamsDrawerStreamType.CONNECT;
    if (isTaggedByCuralateStream) {
      return taggedByCuralatePendingCount;
    } else if (isConnectStream) {
      return connectPendingCount;
    } else {
      return null;
    }
  };

  const sidebarContent = (
    <>
      {isRecommendationsClient && (
        <>
          <SidebarHeaderItem text='Recommended Streams' />

          {showRecommendedSummary && (
            <SidebarPageItemRouteless
              isActive={noSelectedStream}
              onClick={onSummaryClick}
              text='All Recommendations'
            />
          )}

          {recommendationStreams.map((stream) => (
            <SidebarPageItemRouteless
              key={stream.id}
              isActive={selectedStream && selectedStream.id === stream.id}
              linkBadge={
                <Indicator
                  showIf={(pendingItemsCount(stream) ?? 0) > 0}
                  title='You have unmoderated items'
                />
              }
              onClick={() => onStreamClick({ stream })}
              text={getRecommendedStreamName(stream)}
            />
          ))}
        </>
      )}

      <SidebarHeaderItem
        headerAction={
          shouldShowCreateStreamButton ? (
            <ActionButton
              iconName='add'
              label='New Content Stream'
              onClick={onOpenNewStreamModal}
            />
          ) : (
            <></>
          )
        }
        text={`${hasRecommendedStreams ? 'Your' : ''} Media Streams`}
      />

      {alphabetizeLocaleCompare(streams, (stream) => stream.name).map(
        (stream) => (
          <SidebarPageItemRouteless
            key={stream.id}
            isActive={selectedStream && selectedStream.id === stream.id}
            onClick={() => onStreamClick({ stream })}
            text={stream.name}
          />
        ),
      )}
    </>
  );

  return (
    <Sidebar>
      {isRefreshingStreams ? <LoadingPlaceholder /> : sidebarContent}
    </Sidebar>
  );
};

const LoadingPlaceholder = styled(SidebarLoadingPlaceholder)`
  margin-top: 50px;
`;

angular.module('curalate').component('streamsDrawerSidebarReact', {
  bindings: {
    streams: '<',
    isRefreshingStreams: '<',
    isRecommendationsClient: '<',
    hasRecommendedStreams: '<',
    showRecommendedSummary: '<',
    isModerationServices: '<',
    onSummaryClick: '&',
    noSelectedStream: '<',
    recommendationStreams: '<',
    onStreamClick: '&',
    selectedStream: '<',
    onOpenNewStreamModal: '&',
    taggedByCuralatePendingCount: '<',
    connectPendingCount: '<',
    shouldNotAllowUgcStreams: '<',
  },
  controller: makeAngularReactControllerWithScope(
    (scope: { $ctrl: Props } & IScope) => {
      return (
        <StreamsDrawerSidebarReact
          connectPendingCount={scope.$ctrl.connectPendingCount}
          hasRecommendedStreams={scope.$ctrl.hasRecommendedStreams}
          isModerationServices={scope.$ctrl.isModerationServices}
          isRecommendationsClient={scope.$ctrl.isRecommendationsClient}
          isRefreshingStreams={scope.$ctrl.isRefreshingStreams}
          noSelectedStream={scope.$ctrl.noSelectedStream}
          onOpenNewStreamModal={applyScope(
            scope,
            scope.$ctrl.onOpenNewStreamModal,
          )}
          onStreamClick={applyScope(scope, scope.$ctrl.onStreamClick)}
          onSummaryClick={applyScope(scope, scope.$ctrl.onSummaryClick)}
          recommendationStreams={scope.$ctrl.recommendationStreams}
          selectedStream={scope.$ctrl.selectedStream}
          shouldNotAllowUgcStreams={scope.$ctrl.shouldNotAllowUgcStreams}
          showRecommendedSummary={scope.$ctrl.showRecommendedSummary}
          streams={scope.$ctrl.streams}
          taggedByCuralatePendingCount={
            scope.$ctrl.taggedByCuralatePendingCount
          }
        />
      );
    },
  ),
});
